import { Controller } from "@hotwired/stimulus"

// Form filler controller for single-select autocomplete fields
// Displays selection as a removable tag and stores ID in hidden field
//
// Usage:
//   <div data-controller="form-filler"
//        data-form-filler-table-value="artists"
//        data-form-filler-item-id-value="123"
//        data-form-filler-item-val-value="Artist Name">
//     <input type="hidden" name="model[artist_id]" data-form-filler-target="hidden">
//   </div>

export default class extends Controller {
  static targets = ["hidden"]
  static values = {
    table: String,
    itemId: String,
    itemVal: String,
    placeholder: { type: String, default: "" }
  }

  get tableConfig() {
    return {
      artist_id: { table: 'artists', placeholder: 'Artist name...' },
      album_id: { table: 'albums', placeholder: 'Album name...' }
    }
  }

  connect() {
    this.lastTimeout = null
    this.oneSingleItem = null
    this.lastSearchValue = null
    this.lastShowValue = null
    this.lastDataset = null
    this.fetching = false
    this.lookupCache = {}

    // Determine table from data attribute or from hidden field name
    if (!this.hasTableValue) {
      const hiddenName = this.hiddenTarget.name
      const match = hiddenName.match(/\[(\w+)\]$/)
      if (match) {
        const fieldName = match[1]
        const config = this.tableConfig[fieldName]
        if (config) {
          this.tableValue = config.table
          if (!this.hasPlaceholderValue) {
            this.placeholderValue = config.placeholder
          }
        }
      }
    }

    this.createInputField()
    this.createItemsDiv()

    // If there's initial data, show it as a tag
    if (this.itemIdValue && this.itemValValue) {
      this.addTag(this.itemIdValue, this.itemValValue)
    }
  }

  createInputField() {
    this.inputField = document.createElement('input')
    this.inputField.type = 'text'
    this.inputField.classList.add('form-control')
    this.inputField.placeholder = this.placeholderValue
    this.inputField.setAttribute('autocomplete', 'off')
    this.element.appendChild(this.inputField)

    this.boundOnInputFocus = this.onInputFocus.bind(this)
    this.boundOnInputKeydown = this.onInputKeydown.bind(this)
    this.boundOnInput = this.onInput.bind(this)
    this.inputField.addEventListener('focus', this.boundOnInputFocus)
    this.inputField.addEventListener('keydown', this.boundOnInputKeydown)
    this.inputField.addEventListener('input', this.boundOnInput)
  }

  createItemsDiv() {
    this.itemsDiv = document.createElement('div')
    this.itemsDiv.classList.add('items')
    this.element.appendChild(this.itemsDiv)

    this.boundOnItemsKeydown = this.onItemsKeydown.bind(this)
    this.boundOnItemsClick = this.onItemsClick.bind(this)
    this.boundOnFocusout = this.onFocusout.bind(this)
    this.itemsDiv.addEventListener('keydown', this.boundOnItemsKeydown)
    this.itemsDiv.addEventListener('click', this.boundOnItemsClick)
    this.element.addEventListener('focusout', this.boundOnFocusout)
  }

  disconnect() {
    if (this.lastTimeout) {
      clearTimeout(this.lastTimeout)
      this.lastTimeout = null
    }
    if (this.inputField) {
      if (this.boundOnInputFocus) this.inputField.removeEventListener('focus', this.boundOnInputFocus)
      if (this.boundOnInputKeydown) this.inputField.removeEventListener('keydown', this.boundOnInputKeydown)
      if (this.boundOnInput) this.inputField.removeEventListener('input', this.boundOnInput)
    }
    if (this.itemsDiv) {
      if (this.boundOnItemsKeydown) this.itemsDiv.removeEventListener('keydown', this.boundOnItemsKeydown)
      if (this.boundOnItemsClick) this.itemsDiv.removeEventListener('click', this.boundOnItemsClick)
    }
    if (this.boundOnFocusout) this.element.removeEventListener('focusout', this.boundOnFocusout)
  }

  onInputFocus() {
    if (this.inputField.value.length > 0) {
      this.somethingAdded()
    }
  }

  onFocusout(event) {
    if (!this.focusIsInHere(event)) {
      setTimeout(() => this.deactivateItemsDiv(), 500)
    }
  }

  focusIsInHere(event) {
    return event.relatedTarget && (
      (event.relatedTarget.dataset.itemVal && event.relatedTarget.dataset.itemId) ||
      event.relatedTarget === this.inputField
    )
  }

  deactivateItemsDiv() {
    this.itemsDiv.classList.remove('active')
  }

  onInputKeydown(event) {
    if (event.isComposing || event.keyCode === 229) return

    if (event.key === 'ArrowDown') {
      const firstButton = this.itemsDiv.querySelector('button:first-child')
      if (firstButton) firstButton.focus()
      event.stopPropagation()
      event.preventDefault()
    } else if (event.key === 'Enter') {
      if (!this.fetching && this.oneSingleItem) {
        const theItem = this.itemsDiv.firstChild
        if (theItem) {
          this.addTag(theItem.dataset.itemId, theItem.dataset.itemVal)
        }
      }
      event.stopPropagation()
      event.preventDefault()
    }
  }

  onItemsKeydown(event) {
    if (event.key === 'ArrowDown') {
      if (event.target.nextSibling) {
        event.target.nextSibling.focus()
        event.stopPropagation()
        event.preventDefault()
      }
    } else if (event.key === 'ArrowUp') {
      if (event.target.previousSibling) {
        event.target.previousSibling.focus()
      } else {
        this.inputField.focus()
      }
      event.stopPropagation()
      event.preventDefault()
    } else if (event.target.nodeName === 'BUTTON') {
      if (event.key === 'Enter') {
        this.addTag(event.target.dataset.itemId, event.target.dataset.itemVal)
        event.stopPropagation()
        event.preventDefault()
      } else if (event.key === 'Escape') {
        this.inputField.focus()
        this.inputField.select()
        event.stopPropagation()
        event.preventDefault()
      }
    }
  }

  onItemsClick(event) {
    let button = null
    if (event.target.nodeName === 'BUTTON') {
      button = event.target
    } else if (event.target.nodeName === 'EM') {
      button = event.target.closest('button')
    }
    if (button) {
      this.addTag(button.dataset.itemId, button.dataset.itemVal)
    }
  }

  onInput() {
    if (this.fetching) return

    if (this.lastTimeout !== null) {
      clearTimeout(this.lastTimeout)
      this.lastTimeout = null
    }

    const searchValue = this.inputField.value.trimStart().replace(/\s+/g, ' ').toLowerCase()

    if (this.lastShowValue === searchValue) {
      this.somethingAdded()
    } else if (this.lastSearchValue !== null && searchValue.startsWith(this.lastSearchValue) && !this.lastDataset.partial) {
      this.somethingAdded()
    } else {
      this.lastTimeout = setTimeout(() => this.somethingAdded(), 1000)
    }
  }

  hideInputField() {
    this.inputField.style.display = 'none'
    this.deactivateItemsDiv()
  }

  showInputField() {
    this.inputField.style.display = 'block'
    this.inputField.focus()
  }

  addTag(id, val) {
    // Only add if not already there
    if (this.element.querySelector(`div.tag[data-item-id="${id}"]`)) {
      alert("You've already chosen this item")
      return
    }

    const tagDiv = this.makeTag(id, val)
    this.element.insertBefore(tagDiv, this.inputField)
    this.inputField.value = ''
    this.itemsDiv.classList.remove('active')
    this.hideInputField()
    this.hiddenTarget.value = id
  }

  removeTag(tagDiv) {
    this.element.removeChild(tagDiv)
    this.hiddenTarget.value = ''
    if (!this.element.querySelector('div.tag')) {
      this.showInputField()
    }
  }

  makeTag(id, val) {
    const tagDiv = document.createElement('div')
    tagDiv.classList.add('tag')
    tagDiv.dataset.itemId = id
    tagDiv.dataset.itemVal = val
    tagDiv.title = val
    tagDiv.appendChild(document.createTextNode(val))

    const iconSpan = document.createElement('span')
    iconSpan.classList.add('bi', 'bi-x-circle-fill', 'remove')
    iconSpan.addEventListener('click', () => this.removeTag(tagDiv))
    tagDiv.appendChild(iconSpan)

    return tagDiv
  }

  somethingAdded() {
    const searchValue = this.inputField.value.trimStart().replace(/\s+/g, ' ').toLowerCase()
    this.lastTimeout = null

    // Require at least two characters
    if (searchValue.length < 2) return

    this.itemsDiv.style.width = `${this.inputField.clientWidth}px`

    if (this.lastShowValue === searchValue) {
      this.itemsDiv.classList.add('active')
    } else if (this.lastSearchValue !== null && searchValue.startsWith(this.lastSearchValue) && !this.lastDataset.partial) {
      this.showCompletions(searchValue, this.lastDataset)
    } else {
      const cachedValue = this.findInCache(searchValue)
      if (cachedValue) {
        this.lastSearchValue = searchValue
        this.lastDataset = cachedValue
        this.showCompletions(searchValue, this.lastDataset)
      } else {
        this.fetching = true
        fetch(`/autocomplete/autocomplete?table=${this.tableValue}&str=${encodeURIComponent(searchValue)}`)
          .then(response => {
            if (response.ok) {
              return response.json()
            } else {
              throw new Error(`Error: ${response.status}`)
            }
          })
          .then(data => {
            this.lastSearchValue = searchValue
            this.lastDataset = data
            this.lookupCache[searchValue] = data
            this.showCompletions(searchValue, data)
            this.fetching = false
          })
          .catch(err => {
            console.error("Cannot load autocomplete", err)
            this.fetching = false
          })
      }
    }
  }

  findInCache(val) {
    if (this.lookupCache[val]) {
      return this.lookupCache[val]
    }
    // Check for non-partial prefix matches
    for (const k of Object.keys(this.lookupCache)) {
      if (!this.lookupCache[k].partial && val.startsWith(k)) {
        return this.lookupCache[k]
      }
    }
    return null
  }

  showCompletions(searchValue, data) {
    searchValue = searchValue.toLowerCase()
    this.lastShowValue = searchValue
    let itemCount = 0
    let lastBtn = null
    const shownItems = []
    const existingButtons = Array.from(this.itemsDiv.querySelectorAll('button'))

    const all_items = data.exact_items.concat(data.items.filter(function(x) { return !data.exact_items.find(function(y) { return x[0] === y[0] }) } ));

    for (const item of all_items) {
      const id = item[0]
      const val = item[1]
      const pos = val.toLowerCase().indexOf(searchValue)

      if (pos >= 0) {
        const prefix = val.substring(0, pos)
        const matchedPart = val.substring(pos, pos + searchValue.length)
        const suffix = val.substring(pos + searchValue.length)

        const prefixTxt = document.createTextNode(prefix)
        const matchedEm = document.createElement('em')
        matchedEm.appendChild(document.createTextNode(matchedPart))
        const suffixTxt = document.createTextNode(suffix)

        let existingBtn = existingButtons.find(btn => btn.dataset.itemId == id)
        if (existingBtn) {
          lastBtn = existingBtn
          while (existingBtn.firstChild) {
            existingBtn.removeChild(existingBtn.firstChild)
          }
          existingBtn.appendChild(prefixTxt)
          existingBtn.appendChild(matchedEm)
          existingBtn.appendChild(suffixTxt)
        } else {
          const btn = document.createElement('button')
          btn.type = 'button'
          btn.dataset.itemId = id
          btn.dataset.itemVal = val
          btn.appendChild(prefixTxt)
          btn.appendChild(matchedEm)
          btn.appendChild(suffixTxt)

          if (lastBtn) {
            this.itemsDiv.insertBefore(btn, lastBtn.nextSibling)
          } else {
            this.itemsDiv.insertBefore(btn, existingButtons[0])
          }
          lastBtn = btn
        }
        shownItems.push(item)
        itemCount++
      }
    }

    // Remove buttons no longer needed
    for (const btn of existingButtons) {
      if (!shownItems.find(item => item[0] == btn.dataset.itemId)) {
        this.itemsDiv.removeChild(btn)
      }
    }

    if (itemCount === 0) {
      const sorryP = document.createElement('p')
      sorryP.appendChild(document.createTextNode('Nothing found...'))
      this.itemsDiv.appendChild(sorryP)
      this.itemsDiv.style.height = ''
    } else {
      if (itemCount < 5) {
        this.itemsDiv.style.height = ''
      } else {
        this.itemsDiv.style.height = `${24 * 5}px`
      }
      const sorryP = this.itemsDiv.querySelector('p')
      if (sorryP) {
        this.itemsDiv.removeChild(sorryP)
      }
    }

    this.itemsDiv.classList.add('active')
    this.oneSingleItem = (itemCount === 1)
  }
}
